import {buildOfferCheckApiParams} from 'modules/accommodation/buildOfferCheckApiParams'
import {getOfferCheckRoomsHeaders} from 'modules/accommodation/selectors'
import {setProactiveOfferCheckResolved} from 'modules/accommodation/slice'
import {
  getCommonPaymentPageLandingParams,
  getStructuredDataMarkupOffer
} from 'modules/searchApi/selectors'
import {Dispatch} from 'redux'
import {AnyAction} from 'redux'
import {RootState} from 'store'

import {getBovioApiKey} from '@vio/core/src/experiments/modules/selectors'
import {offerCheckApi} from '@vio/core/src/offer/services/offerCheckApi'
import {Offer} from '@vio/core/src/offer/types/offer'
import {Room} from '@vio/core/src/room/types/room'
import {roomSearchSucceeded} from '@vio/core/src/sapi/services/searchApi/action'

import {MiddlewareType} from '../middlewareList'

const triggerOfferCheck = (
  dispatch: Dispatch,
  state: RootState,
  offer: Offer,
  room: Room
): boolean => {
  const commonPaymentPageParams = getCommonPaymentPageLandingParams(state)
  const headers = getOfferCheckRoomsHeaders(state)
  const apiKey = getBovioApiKey(state)
  const apiParams = buildOfferCheckApiParams({
    headers,
    commonPaymentPageParams: commonPaymentPageParams,
    offer,
    room,
    apiKey
  })
  if (!apiParams) return false

  // RTK Query's initiate types don't support direct middleware dispatch; casts are workarounds.
  const initiateResult = offerCheckApi.endpoints.getOfferCheck.initiate(
    {
      ...apiParams,
      options: {
        offerId: offer.id,
        isProactiveCheck: true
      }
    },
    {subscribe: true}
  )
  dispatch(initiateResult as unknown as AnyAction)
  return true
}

const middleware: MiddlewareType = store => next => action => {
  const result = next(action)
  const state = store.getState()
  const {dispatch} = store

  /**
   * 1. When a new search succeeds, check the offer we serve SDM for - the
   * preferred-rate offer (only on experiment side B). This must be the exact
   * offer emitted in Structured Data Markup so the validated price matches.
   *
   * We use roomSearchSucceeded (not getRoomsPolling.matchFulfilled) because the latter
   * fires when the queryFn returns with placeholder data (rooms: []). The actual rooms
   * are populated asynchronously via updateCachedData in the SAPI callbacks. roomSearchSucceeded
   * is dispatched only after onComplete, when the cache already has the real room data.
   */
  if (roomSearchSucceeded.match(action)) {
    const {isPreClickOfferCheckValidationEnabled} = action.payload

    if (isPreClickOfferCheckValidationEnabled) {
      const emitted = getStructuredDataMarkupOffer(state)
      const triggered = emitted?.offer
        ? triggerOfferCheck(dispatch, state, emitted.offer, emitted.room)
        : false

      // Keep the SDM waiting only while a proactive check is actually in flight
      // if there is nothing to check, resolve immediately so it isn't blocked
      dispatch(setProactiveOfferCheckResolved(!triggered))
    }
  }

  /**
   * 2️. Once a proactive check settles (success or failure), unblock the SDM.
   */
  const isProactiveCheckSettled =
    (offerCheckApi.endpoints.getOfferCheck.matchFulfilled(action) ||
      offerCheckApi.endpoints.getOfferCheck.matchRejected(action)) &&
    action.meta.arg.originalArgs.options?.isProactiveCheck

  if (isProactiveCheckSettled) {
    dispatch(setProactiveOfferCheckResolved(true))
  }

  return result
}
export default middleware
